/**
 * Chasing — one vocabulary for the collection loop.
 *
 * The list, the row column, the detail header and the reminder composer all
 * describe the same three states (never chased / due a follow-up / chased
 * recently). They read these helpers so they cannot describe them differently:
 * "12d ago" must mean the same thing in the LAST CHASED column as it does in
 * the invoice header.
 */
import { formatDate, formatDateShort } from '../../lib/format';
import type { InvoiceChase, SendRecord } from './api';

export const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  copied: 'Copied text',
  manual: 'Logged manually',
};

/** How the contact left: past-tense, for a timeline entry. */
export const CHANNEL_VERBS: Record<string, string> = {
  email: 'opened in your mail app',
  copied: 'copied to the clipboard',
  manual: 'logged manually',
};

/** Whole days between an ISO timestamp and today, in the viewer's timezone. */
export function daysAgo(iso: string, now = new Date()): number {
  const then = new Date(iso);
  const a = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const b = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
  return Math.max(0, Math.round((a - b) / 86_400_000));
}

/**
 * Whole days from today to a YYYY-MM-DD date: positive ahead, 0 today,
 * negative behind. `daysAgo` clamps at zero and cannot tell a future date
 * from today's, which is exactly the distinction a follow-up date needs.
 */
export function daysUntil(date: string, now = new Date()): number {
  const then = new Date(`${date}T12:00:00`);
  const a = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const b = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
  return Math.round((b - a) / 86_400_000);
}

/** "today" · "yesterday" · "12d ago" — compact enough for a table cell. */
export function agoLabel(days: number): string {
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

/**
 * "Chased 3× · last 12d ago" — the one line that says whether this debt is
 * being worked. Returns null when there is nothing to say (a paid invoice
 * nobody ever needed to chase).
 */
export function chaseLabel(chase: InvoiceChase, isLate: boolean): string | null {
  if (chase.count === 0) {
    // A hold placed before the first chase is still the most important fact
    // about this row — "Never chased" would read as an oversight when it is a
    // decision somebody made on purpose.
    if (chase.held) return 'On hold';
    return isLate ? 'Never chased' : null;
  }
  const times = chase.count === 1 ? 'Chased once' : `Chased ${chase.count}×`;
  const days = chase.daysSince ?? 0;
  // A PAUSED invoice has left the automatic queue: the scheduler will not send
  // a fifth identical email, and the row has to say so or the reader will keep
  // waiting for one. It is the same fact the chase bar counts above the table.
  //
  // HELD wins over paused when both are true, because it is the answer to a
  // different question: paused is the machine giving up, held is a person
  // deciding. The remedy differs, so the word must.
  const suffix = chase.held ? ' · on hold' : chase.paused ? ' · paused' : '';
  return `${times} · last ${agoLabel(days)}${suffix}`;
}

/**
 * "Follow up today" / "Follow up Aug 19" / "Follow up overdue since Jul 3" —
 * what to do next, not what happened. Null when the invoice is settled and
 * nothing is owed.
 *
 * A follow-up that came due five weeks ago is not "today": saying so hid how
 * far behind the collection loop had fallen, and made the row disagree with
 * the date the API actually returned. Late is said out loud, with the date.
 */
export function followUpLabel(
  chase: InvoiceChase,
  isLate: boolean,
  options: { short?: boolean } = {},
): string | null {
  if (!chase.nextFollowUp) return null;
  // The cadence still computes a date for a held invoice, but nothing will act
  // on it. Printing "Follow up today" next to a hold somebody placed on purpose
  // is the screen contradicting itself about what the machine is going to do.
  if (chase.held) return 'Automatic chasing on hold';
  const when = options.short
    ? formatDateShort(chase.nextFollowUp)
    : formatDate(chase.nextFollowUp);
  if (!isLate) return `Follow up ${when}`;
  const days = daysUntil(chase.nextFollowUp);
  if (days > 0) return `Follow up ${when}`;
  return days === 0 ? 'Follow up today' : `Follow up overdue since ${when}`;
}

/**
 * Was this invoice ever actually delivered, and to whom — "Sent to
 * ap@redwood.example · 7d ago". The question a client's "we never received
 * it" asks, answered without opening anything.
 */
export function sentLabel(sent: SendRecord | null, options: { short?: boolean } = {}): string | null {
  if (!sent) return null;
  const when = agoLabel(daysAgo(sent.at));
  if (options.short) return `sent ${when}`;
  return sent.to ? `Sent to ${sent.to} · ${when}` : `Sent · ${when}`;
}
